import { queryClient } from '@/client/query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

const navbarHeightQueryKey = ['navbarHeight', 'get'];
const intercomStatusQueryKey = ['intercom', 'status'];
const footerVisibilityQueryKey = ['footer', 'visibility'];
// This next array is to avoid showing the footer for a fraction
// of a second before the useSetFooterVisible has been able to fire
const footerVisibilityDefaultExceptions = ['testing-plan/complete-test'];

export function useGetNavbarHeight() {
  return useQuery({
    queryKey: navbarHeightQueryKey,
    initialData: () => 56,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useSetNavbarHeight() {
  return useMutation({
    mutationFn: (height: number) => Promise.resolve(height),
    onSuccess: (height: number) => {
      queryClient.setQueryData(navbarHeightQueryKey, height);
    },
  });
}

export function useGetIntercomIsVisible() {
  return useQuery({
    queryKey: intercomStatusQueryKey,
    initialData: () => true,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: 'always',
  });
}

export function useSetIntercomIsVisible() {
  return useMutation({
    mutationFn: (status: boolean) => Promise.resolve(status),
    onSuccess: (status: boolean) => {
      queryClient.setQueryData(intercomStatusQueryKey, status);
    },
  });
}

export function useGetFooterIsVisible() {
  return useQuery({
    queryKey: footerVisibilityQueryKey,
    initialData: () =>
      !footerVisibilityDefaultExceptions.find((exception) => window.location.pathname.includes(exception)),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: 'always',
  });
}

export function useSetFooterIsVisible() {
  return useMutation({
    mutationFn: (isVisible: boolean) => Promise.resolve(isVisible),
    onSuccess: (isVisible: boolean) => {
      queryClient.setQueryData(footerVisibilityQueryKey, isVisible);
    },
  });
}

/**
 * hides the footer and intercom when fullscreen experience is needed
 * @param shouldSkip - can be used for storybook scenarios
 */
export function useFullScreen(shouldSkip = false) {
  const mutateFooterIsVisible = useSetFooterIsVisible();
  const mutateIntercomVisibility = useSetIntercomIsVisible();

  // Create stable references to the async functions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setFooterVisible = useMemo(() => mutateFooterIsVisible.mutateAsync, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setIntercomVisible = useMemo(() => mutateIntercomVisibility.mutateAsync, []);

  // Footer and Intercom visibility
  useEffect(() => {
    if (shouldSkip) {
      return; // Skip footer manipulation for stories
    }

    // Only execute once when component mounts
    let isCancelled = false;

    const setupVisibility = async () => {
      if (!isCancelled) {
        await setFooterVisible(false);
        await setIntercomVisible(false);
      }
    };

    setupVisibility();

    return () => {
      isCancelled = true;
      setFooterVisible(true);
      setIntercomVisible(true);
    };
  }, [shouldSkip, setFooterVisible, setIntercomVisible]);
}
